import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { EmojiEmotions, ThumbUp, Favorite, Star } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  useSendReaction,
  ReactionType,
} from "../../../services/stagemode_api/viewers/viewers_api";

// Styled dark card section
const InteractionSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: "16px",
  backgroundColor: "#1A1A1A",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.6)",
  fontFamily: "Lexend, sans-serif",
  color: "#fff",
}));

// Styled reaction buttons
const EmojiButton = styled(IconButton)(({ theme }) => ({
  width: 56,
  height: 56,
  backgroundColor: "#2A2A2A",
  color: "#fff",
  borderRadius: "50%",
  border: "1px solid #333",
  transition: "transform 0.2s ease, background 0.3s ease",
  "&:hover": {
    transform: "scale(1.2)",
    backgroundColor: "#e50914", // Netflix red
    color: "#fff",
  },
}));

const QuickReactions: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const [activeReaction, setActiveReaction] = useState<ReactionType | null>(
    null
  );
  const [clicked, setClicked] = useState<ReactionType | null>(null);

  const { mutateAsync: sendReaction } = useSendReaction(bookingId ?? "");

  const handleReaction = async (type: ReactionType) => {
    try {
      if (activeReaction === type) {
        // If clicking the same reaction, remove it
        setActiveReaction(null);
        await sendReaction({ type, status: 0 });
      } else {
        // If clicking a different reaction
        if (activeReaction) {
          // Remove the previous reaction count
          await sendReaction({ type, status: 0 });
        }
        // Set new active reaction
        setActiveReaction(type);
        await sendReaction({ type, status: 1 });
      }
      setClicked(type);
      setTimeout(() => setClicked(null), 400);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const renderIcon = (icon: React.ReactElement, type: ReactionType) => (
    <Tooltip title={type}>
      <motion.div
        animate={clicked === type ? { scale: [1, 1.3, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        <EmojiButton
          onClick={() => handleReaction(type)}
          sx={{
            backgroundColor: activeReaction === type ? "#e50914" : "#2A2A2A",
          }}
        >
          {icon}
        </EmojiButton>
      </motion.div>
    </Tooltip>
  );

  return (
    <InteractionSection>
      <Box display="flex" alignItems="center" mb={2}>
        <EmojiEmotions sx={{ mr: 1, color: "#e50914" }} />
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Lexend",
            fontWeight: 600,
            fontSize: "20px",
            color: "#fff",
          }}
        >
          Quick Reactions
        </Typography>
      </Box>

      <Stack direction="row" spacing={4} justifyContent="center">
        {renderIcon(<ThumbUp />, "Like")}
        {renderIcon(<Favorite />, "Heart")}
        {renderIcon(<Star />, "Star")}
      </Stack>
    </InteractionSection>
  );
};

export default QuickReactions;
